import React from "react";
import { Link } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowRight, PlayCircle } from "lucide-react";
import { useLang } from "../i18n";
import { IMAGES } from "../data/content";
import { CircuitBackground, Particles } from "../components/fx/Backgrounds";

const HEAD = {
  ro: ["Energie Inteligentă.", "Automatizări Moderne.", "Siguranță Garantată."],
  en: ["Intelligent Energy.", "Modern Automation.", "Guaranteed Safety."],
};

const lineVariant = {
  hidden: { y: "110%" },
  show: (i) => ({ y: "0%", transition: { duration: 0.9, delay: 0.25 + i * 0.14, ease: [0.22, 1, 0.36, 1] } }),
};

export const Hero = () => {
  const { lang, ui, L } = useLang();
  const { scrollY } = useScroll();
  const y = useTransform(scrollY, [0, 600], [0, 120]);

  const sub = L({
    ro: "VOLTPDR proiectează și implementează instalații electrice, soluții smart home și automatizări profesionale pentru locuințe, spații comerciale și industrie.",
    en: "VOLTPDR designs and implements electrical systems, smart home solutions and professional automation for homes, commercial spaces and industry.",
  });

  return (
    <section className="relative isolate flex min-h-[100svh] min-h-[100dvh] items-center overflow-hidden pt-24">
      {/* Parallax background */}
      <motion.div style={{ y }} className="absolute inset-0 scale-110">
        <img src={IMAGES.heroPrimary} alt="" className="h-full w-full object-cover" loading="eager" />
      </motion.div>
      <div className="absolute inset-0 bg-gradient-to-b from-ink/70 via-ink/80 to-ink" />
      <div className="absolute inset-0 bg-[radial-gradient(circle_at_20%_20%,rgba(33,90,165,0.35),transparent_55%)]" />
      <div className="absolute inset-0 grid-bg opacity-40" />
      <CircuitBackground className="opacity-70" />
      <Particles count={12} />

      <motion.div className="container-pdr relative z-10">
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          className="mb-7 inline-flex items-center gap-3 rounded-full border border-white/15 bg-white/5 px-4 py-2 backdrop-blur"
        >
          <span className="h-2 w-2 animate-pulse-glow rounded-full bg-glow-2 shadow-[0_0_10px_2px_rgba(112,214,255,0.9)]" />
          <span className="font-mono text-[11px] uppercase tracking-[0.28em] text-white/80">
            {lang === "ro" ? "Engineering · Automatizări · Instalații" : "Engineering · Automation · Installations"}
          </span>
        </motion.div>

        <h1 className="max-w-5xl font-display text-[2.6rem] font-extrabold leading-[1.02] sm:text-6xl lg:text-[5.2rem]">
          {HEAD[lang].map((line, i) => (
            <span key={i} className="block overflow-hidden py-[0.06em]">
              <motion.span
                custom={i}
                variants={lineVariant}
                initial="hidden"
                animate="show"
                className={`block ${i === 2 ? "text-gradient-glow text-shadow-glow" : "text-white"}`}
              >
                {line}
              </motion.span>
            </span>
          ))}
        </h1>

        <motion.p
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.9, duration: 0.8 }}
          className="mt-8 max-w-2xl text-base leading-relaxed text-white/70 md:text-lg"
        >
          {sub}
        </motion.p>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 1.05, duration: 0.8 }}
          className="mt-10 flex flex-wrap items-center gap-4"
        >
          <Link
            to="/contact"
            data-testid="hero-quote-btn"
            className="btn-glow group inline-flex items-center gap-2 rounded-full bg-brand px-7 py-4 text-sm font-semibold text-ink transition-transform duration-300 hover:scale-[1.03] glow-ring"
          >
            {ui.common.requestQuote}
            <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
          </Link>
          <Link
            to="/portofoliu"
            data-testid="hero-portfolio-btn"
            className="group inline-flex items-center gap-2 rounded-full border border-white/20 px-7 py-4 text-sm font-semibold text-white transition-colors duration-300 hover:border-glow-2/60 hover:bg-white/5"
          >
            <PlayCircle className="h-5 w-5 text-glow-2" />
            {ui.common.viewPortfolio}
          </Link>
        </motion.div>
      </motion.div>

      <div className="pointer-events-none absolute bottom-0 left-0 right-0 h-32 bg-gradient-to-t from-ink to-transparent" />
    </section>
  );
};
